import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const { FiX, FiLayers, FiCheck, FiUploadCloud, FiFile, FiTrash2 } = FiIcons;

const AssetModal = ({ isOpen, onClose, onSave, initialData, properties }) => {
  const defaults = {
    propertyId: '',
    name: '',
    type: 'Appliance',
    brand: '',
    model: '',
    serialNumber: '',
    purchaseDate: '',
    purchasePrice: '',
    installDate: format(new Date(), 'yyyy-MM-dd'),
    warrantyExpiry: '',
    warrantyProvider: '',
    warrantyNotes: '',
    serviceIntervalMonths: 6,
    lastServiceDate: format(new Date(), 'yyyy-MM-dd'),
    receiptFileName: '',
    receiptData: '',
    notes: ''
  };

  const [formData, setFormData] = useState(defaults);

  const assetTypes = ['Appliance', 'HVAC', 'Security', 'Plumbing', 'Electrical', 'Furniture', 'Electronics', 'Other'];

  useEffect(() => {
    if (initialData) {
      setFormData({ ...defaults, ...initialData });
    } else {
      setFormData(defaults);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({
        ...prev,
        receiptFileName: file.name,
        receiptData: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeReceipt = () => {
    setFormData(prev => ({ ...prev, receiptFileName: '', receiptData: '' }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <SafeIcon icon={FiLayers} size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{initialData?.id ? 'Update Asset' : 'Register Asset'}</h2>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Inventory Management</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Property & Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Property *</label>
              <select required value={formData.propertyId} onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                <option value="">Select Property...</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Asset Name *</label>
              <input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="e.g. Kitchen Refrigerator" />
            </div>
          </div>

          {/* Type & Brand & Model */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Type</label>
              <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
                {assetTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Brand</label>
              <input value={formData.brand} onChange={e => setFormData({ ...formData, brand: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="e.g. Samsung" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Model</label>
              <input value={formData.model} onChange={e => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="e.g. RF28R7351SR" />
            </div>
          </div>

          {/* Serial Number & Purchase Info */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Serial Number</label>
              <input value={formData.serialNumber} onChange={e => setFormData({ ...formData, serialNumber: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none font-mono" placeholder="SN-XXXX-XXXX" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Purchase Date</label>
              <input type="date" value={formData.purchaseDate} onChange={e => setFormData({ ...formData, purchaseDate: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Purchase Price ($)</label>
              <input type="number" min="0" step="0.01" value={formData.purchasePrice} onChange={e => setFormData({ ...formData, purchasePrice: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="0.00" />
            </div>
          </div>

          {/* Install & Service Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Install Date</label>
              <input type="date" value={formData.installDate} onChange={e => setFormData({ ...formData, installDate: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Last Service Date</label>
              <input type="date" value={formData.lastServiceDate} onChange={e => setFormData({ ...formData, lastServiceDate: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Service Interval (Months)</label>
              <input type="number" min="1" value={formData.serviceIntervalMonths} onChange={e => setFormData({ ...formData, serviceIntervalMonths: Number(e.target.value) })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" />
            </div>
          </div>

          {/* Warranty Section */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Warranty Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Warranty Expiry</label>
                <input type="date" value={formData.warrantyExpiry} onChange={e => setFormData({ ...formData, warrantyExpiry: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none bg-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Warranty Provider</label>
                <input value={formData.warrantyProvider} onChange={e => setFormData({ ...formData, warrantyProvider: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none bg-white" placeholder="Provider or policy #" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Warranty Details / Notes</label>
              <textarea rows={2} value={formData.warrantyNotes} onChange={e => setFormData({ ...formData, warrantyNotes: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none bg-white resize-none" placeholder="Coverage details, claim instructions, etc." />
            </div>
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">Receipt / Documentation</label>
            {formData.receiptFileName ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <SafeIcon icon={FiFile} size={16} className="text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-green-800 truncate">{formData.receiptFileName}</span>
                </div>
                <button type="button" onClick={removeReceipt} className="p-1 text-green-400 hover:text-red-500 transition-colors shrink-0">
                  <SafeIcon icon={FiTrash2} size={14} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors">
                <SafeIcon icon={FiUploadCloud} size={24} className="text-gray-300 mb-2" />
                <span className="text-sm font-medium text-gray-500">Click to upload receipt or documentation</span>
                <span className="text-xs text-gray-400 mt-1">PDF, JPG, PNG up to 5MB</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleFileUpload} />
              </label>
            )}
          </div>

          {/* General Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes</label>
            <textarea rows={2} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none resize-none" placeholder="Any additional notes..." />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
          <button
            onClick={() => { onSave({ ...formData, purchasePrice: Number(formData.purchasePrice) || 0 }); onClose(); }}
            className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm"
          >
            <SafeIcon icon={FiCheck} size={15} /> {initialData?.id ? 'Update Asset' : 'Register Asset'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetModal;
